const encoder = new TextEncoder();

/** Minimum accepted secret length for HMAC keys, measured in characters. */
export const HMAC_SHA256_KEY_MIN_LENGTH = 32;

function assertHmacKey(key: string): void {
  if (key.length < HMAC_SHA256_KEY_MIN_LENGTH) {
    throw new Error(
      `HMAC-SHA256 key must be at least ${HMAC_SHA256_KEY_MIN_LENGTH} characters, got ${key.length}`,
    );
  }
}

async function importHmacKey(key: string): Promise<CryptoKey> {
  assertHmacKey(key);
  return crypto.subtle.importKey(
    "raw",
    encoder.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

/** Signs a string with HMAC-SHA256 and returns the lowercase hexadecimal digest. */
export async function signHmacSha256(body: string, key: string): Promise<string> {
  const cryptoKey = await importHmacKey(key);
  const signature = await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(body));
  return bytesToHex(new Uint8Array(signature));
}

/** Compares strings without stopping at the first different character. */
export function timingSafeEqualString(a: string, b: string): boolean {
  const maxLength = Math.max(a.length, b.length);
  let difference = a.length ^ b.length;

  for (let index = 0; index < maxLength; index += 1) {
    difference |= (a.charCodeAt(index) || 0) ^ (b.charCodeAt(index) || 0);
  }

  return difference === 0;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
